/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import React, { ReactElement } from 'react';
import { SectionRow, UaPlaceholder } from 'templates/pubSub/types/TemplateType';
import { TemplateContext } from 'templates/pubSub/types/TemplateContextType';
import { usePubSubNode, usePubSubNodes } from 'utilities/PubSub/PubSubHooks';
import TemplateHelperPubSub from 'utilities/TemplateHelperPubSub';
import { NodeIdentifier } from 'utilities/PubSub/PubSubAddressSpace';
import SectionRowContent from './SectionRowContent';

interface ISectionRowPlaceholderProps {
  templateContext: TemplateContext;
  row: SectionRow;
  cols: string;
  placeholder: UaPlaceholder;
  placeholderIndex: number;
  startingNodeId?: NodeIdentifier;
}

export default function SectionRowPlaceholder(props: ISectionRowPlaceholderProps): ReactElement {
  const filter = TemplateHelperPubSub.createFilter(props.templateContext.namespaces, props.placeholder.variablePath);
  const node = usePubSubNode(filter, props.startingNodeId);
  const placeholderFilter = TemplateHelperPubSub.createReferenceAndTypeFilter(
    props.templateContext.namespaces,
    props.placeholder.reference,
    props.placeholder.type,
  );
  const placeholderNodes = usePubSubNodes(placeholderFilter, node);

  const nextPlaceholder =
    props.row.placeholders && props.row.placeholders.length > props.placeholderIndex + 1
      ? props.row.placeholders[props.placeholderIndex + 1]
      : undefined;

  return (
    <>
      {placeholderNodes.map((placeholderNode, placeholderNodeIndex) => (
        <React.Fragment key={TemplateHelperPubSub.getPlaceholderObjectKey(props.row.id, placeholderNodeIndex)}>
          {nextPlaceholder ? (
            <SectionRowPlaceholder
              templateContext={props.templateContext}
              cols={props.cols}
              row={props.row}
              placeholder={nextPlaceholder}
              placeholderIndex={props.placeholderIndex + 1}
              startingNodeId={placeholderNode}
            />
          ) : (
            <SectionRowContent
              templateContext={props.templateContext}
              cols={props.cols}
              row={props.row}
              startingNodeId={placeholderNode}
            />
          )}
        </React.Fragment>
      ))}
    </>
  );
}
